import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getApplicationsBySearch,
  getApplicationSearchFilters,
  getCommercialOdApplicationsBySearch,
  getWCTLApplicationBySearch,
} from "../../actions/application.action";
import Badge from "../../components/badge";
import DateRange from "../../components/date-range";
import { SearchBox } from "../../components/input-functions";
import MultiSelect, {
  convertFilterArrayToObject,
  convertFilterObjectToArray,
  MultiSelectOption,
} from "../../components/multi-select";
import Pagination from "../../components/pagination";
import Table, { TableColumns } from "../../components/table";
import Tabs, { TabItem } from "../../components/tabs";
import {
  CommercialODApplicationDetails,
  RetailODApplicationDetails,
  TermLoanApplicationDetails,
} from "../../dto";
import useQueryParams from "../../hooks/useQueryParams";
import useScreenName from "../../hooks/useScreenName";
import dispatch from "../../middleware";
import {
  ApplicationStatusType,
  ApplicationType,
  RoutePaths,
  SearchFilter,
} from "../../models";
import { ReduxState } from "../../reducers";
import {
  clearFormData,
  dateToDDMMYYYY,
  downloadCSV,
  isLoadingActive,
  splitKeyWithSpace,
} from "../../utils";

const tabItems: TabItem<ApplicationType>[] = [
  {
    label: "Term Loan Application",
    value: "TERM_LOAN_APP",
  },
  {
    label: "Earned Wage Access Application",
    value: "RETAIL_OD_APP",
  },
];

const applicationTypeToRoutePathMap: Record<ApplicationType, RoutePaths> = {
  COMMERCIAL_OD_APP: RoutePaths.COMMERCIAL_LOAN_APPLICATION,
  WORKING_CAPITAL_TERM_LOAN_APP: RoutePaths.WCTL_LOAN_APPLICATION,
  RETAIL_OD_APP: RoutePaths.APPLICATIONS,
  TERM_LOAN_APP: RoutePaths.APPLICATIONS,
};

// Need to render `approvedAmount` field only for TERM_LOAN, thus used the conditional spreading
function getColumns(type: ApplicationType): TableColumns[] {
  if (type === "COMMERCIAL_OD_APP") {
    return [
      { field: "_id", label: "Application ID" },
      { field: "name", label: "Name" },
      { field: "createdAt", label: "Date Of Application" },
      { field: "irpa", label: "IRPA" },
      { field: "pf", label: "PF" },
      { field: "sanctionedAmount", label: "Sanctioned Amount" },
      { field: "status", label: "Status" },
    ];
  }

  if (type === "WORKING_CAPITAL_TERM_LOAN_APP") {
    return [
      { field: "_id", label: "Application ID" },
      { field: "productId", label: "Product ID" },
      { field: "irpa", label: "IRPA" },
      { field: "pf", label: "PF" },
      {
        field: "approvedAmount",
        label: "Approved Amount",
      },
      { field: "moratoriumInMonths", label: "MIM" },
      { field: "status", label: "status" },
    ];
  }

  return [
    { field: "_id", label: "Application ID" },
    { field: "userName", label: "User Name" },
    { field: "createdAt", label: "Date Of Application" },
    ...(type === "TERM_LOAN_APP"
      ? [{ field: "approvedAmount", label: "Sanctioned Amount" }]
      : [{ field: "maxAmount", label: "Sanctioned Amount" }]),
    // { field: "irpa", label: "IRPA" },
    // { field: "pf", label: "PF" },
    ...(type === "TERM_LOAN_APP"
      ? [
          {
            field: "tenureInMonths",
            label: "Tenure (months)",
            // INFO: `as any` is to resolve the TS issue
            // TODO: Figure out proper fix
            style: { textAlign: "center" as any },
          },
        ]
      : []),
    { field: "status", label: "Status" },
  ];
}

type SearchQueryType = {
  applicationType: ApplicationType;
  pageNo: number;
  searchText: string;
  numberOfEntitiesPerPage: number;
  status: string[];
  source: string[];
  startDate: string;
  endDate: string;
};

const DEFAULT_SEARCH_QUERY_STATE: SearchQueryType = {
  applicationType: "TERM_LOAN_APP",
  pageNo: 1,
  searchText: "",
  numberOfEntitiesPerPage: 10,
  status: [],
  source: [],
  startDate: "",
  endDate: "",
};

export default function ApplicationsScreen() {
  useScreenName("Applications");
  const storeDispatch = useDispatch();

  const applications:
    TermLoanApplicationDetails[] | RetailODApplicationDetails[] = useSelector(
    (state: ReduxState) => state.application.applications,
  );
  const applicationSearchFilters: Record<ApplicationType, SearchFilter[]> =
    useSelector((state: ReduxState) => state.application.searchFilters);
  const loadingQueue = useSelector(
    (state: ReduxState) => state.application.loadingQueue,
  );

  const [
    {
      applicationType,
      pageNo,
      searchText,
      numberOfEntitiesPerPage,
      status,
      source,
      startDate,
      endDate,
    },
    setSearchQuery,
  ] = useQueryParams<SearchQueryType>(DEFAULT_SEARCH_QUERY_STATE);

  const filterOptions = useMemo(() => {
    const filters = applicationSearchFilters[applicationType];
    if (!Array.isArray(filters)) return [];
    return filters.reduce(
      (accum, group) => [
        ...accum,
        ...group.values.map((value) => ({
          group: group.name,
          value,
          label: splitKeyWithSpace(value),
        })),
      ],
      [] as MultiSelectOption[],
    );
  }, [applicationSearchFilters, applicationType]);

  useEffect(() => {
    const filters = applicationSearchFilters[applicationType];
    if (!Array.isArray(filters)) return;
    if (filters.length > 0) return;
    dispatch(storeDispatch, getApplicationSearchFilters(applicationType));
  }, [applicationType]);

  useEffect(() => {
    if (applicationType === "COMMERCIAL_OD_APP") {
      dispatch(
        storeDispatch,
        getCommercialOdApplicationsBySearch(
          clearFormData({
            numberOfEntitiesPerPage,
            pageNo,
            searchText,
            applicationType,
            status,
            source,
            startDate,
            endDate,
          }),
        ),
      );
      return;
    }

    if (applicationType === "WORKING_CAPITAL_TERM_LOAN_APP") {
      dispatch(
        storeDispatch,
        getWCTLApplicationBySearch(
          clearFormData({
            numberOfEntitiesPerPage,
            pageNo,
            applicationType,
            status,
            source,
          }),
        ),
      );
      return;
    }

    dispatch(
      storeDispatch,
      getApplicationsBySearch(
        clearFormData({
          numberOfEntitiesPerPage,
          pageNo,
          searchText,
          applicationType,
          status,
          source,
          startDate,
          endDate,
        }),
      ),
    );
  }, [
    pageNo,
    searchText,
    applicationType,
    numberOfEntitiesPerPage,
    status,
    source,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (applicationType === "COMMERCIAL_OD_APP" ||
      applicationType === "WORKING_CAPITAL_TERM_LOAN_APP"
    )
      return;
    // Pagination count API removed for performance; using hasNextPage instead
  }, [applicationType, searchText, status, source, startDate, endDate]);

  function handleSearch(searchText: string) {
    setSearchQuery((prevState) => ({ ...prevState, searchText, pageNo: 1 }));
  }

  function handleFilterChanges(options: MultiSelectOption[]) {
    const filterObject = convertFilterArrayToObject(options);
    setSearchQuery((prevState) => ({
      ...prevState,
      ...{ status: [], source: [] },
      ...filterObject,
      pageNo: 1,
    }));
  }

  //INFO: empty string is allowed
  function handleDateChange(fieldName: string, val: string) {
    setSearchQuery((prevState) => ({ ...prevState, [fieldName]: val }));
  }

  function getStatusBadge(status: ApplicationStatusType) {
    if (status === "APPROVED") return <Badge type="success">Approved</Badge>;
    if (status === "REJECTED") return <Badge type="reject">Rejected</Badge>;
    return (
      <Badge type="pending" capitalize>
        {splitKeyWithSpace(status).toLowerCase()}
      </Badge>
    );
  }
  function getIdLink(path: RoutePaths, id?: string) {
    if (!id) return;
    return (
      <Link to={`${path}/${id}`} className="table-link">
        {id}
      </Link>
    );
  }
  function getEmployeeNameAsLink(userId?: string, userName?: string) {
    if (!userName || !userId) return "-";
    return (
      <Link to={`users/individual/${userId}`} className="table-link">
        {userName}
      </Link>
    );
  }

  function getTableData(
    applications: (TermLoanApplicationDetails | RetailODApplicationDetails)[],
  ) {
    const path = applicationTypeToRoutePathMap[applicationType];
    return applications.map(
      ({
        _id,
        user,
        createdAt,
        approvedAmount,
        maxAmount,
        productId,
        moratoriumInMonths,
        irpa,
        entitySnapshot,
        pf,
        source,
        tenureInMonths,
        status,
        sanctionedAmount,
        tenor,
      }: (
        | TermLoanApplicationDetails
        | RetailODApplicationDetails
        | CommercialODApplicationDetails
      ) & {
        [x: string]: any;
      }) => ({
        _id: getIdLink(path, _id),
        name: entitySnapshot?.snapshot?.name ?? "-",
        userName: getEmployeeNameAsLink(user?._id, user?.employeeName),
        employerName: user?.employerName ?? "-",
        createdAt: createdAt ? dateToDDMMYYYY(createdAt) : "-",
        maxAmount: maxAmount || "-",
        approvedAmount: approvedAmount || "-",
        productId,
        moratoriumInMonths,
        irpa,
        pf,
        tenureInMonths,
        // INFO: Currently in backend we don't have tenor so that adding it in frontend
        tenor: 12,
        source: splitKeyWithSpace(source) || "-",
        status: getStatusBadge(
          typeof status === "string" ? status : status.data,
        ),
        sanctionedAmount: sanctionedAmount,
      }),
    );
  }
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const rows = applications.map(
      (
        application: (
          TermLoanApplicationDetails | RetailODApplicationDetails
        ) & {
          [x: string]: any;
        },
      ) => ({
        "User Id": application.user?._id || "NA",
        "User Name": application.user?.employeeName || "NA",
        Status: application.status?.data || "NA",
        Amount: application.approvedAmount || application.maxAmount,
        Tenure: application?.tenureInMonths || "NA",
        PF: application?.pf || "NA",
      }),
    );
    //INFO: undefined is passed to create columns labels from rows keys
    downloadCSV(rows, undefined, "applications-report");
  }
  return (
    <>
      <div className="dashboard-container p-3">
        <Tabs
          items={tabItems}
          disable={isLoadingActive(loadingQueue)}
          value={applicationType}
          onChange={(value) => {
            setSearchQuery({
              ...DEFAULT_SEARCH_QUERY_STATE,
              applicationType: value,
            });
          }}
        />
      </div>
      <div className="dashboard-container">
        {applicationType === "COMMERCIAL_OD_APP" ||
        applicationType === "WORKING_CAPITAL_TERM_LOAN_APP" ? null : (
          <div className="d-flex flex-wrap mb-4" style={{ gap: "2px" }}>
            <div
              style={{ display: "flex", flexWrap: "wrap", marginRight: "16px" }}
            >
              <div className="search-input-user mr-lg-3 mb-4 mb-lg-0">
                <MultiSelect
                  label="Filter"
                  options={filterOptions}
                  groupped
                  value={convertFilterObjectToArray({ source, status })}
                  onChange={handleFilterChanges}
                />
              </div>
              <SearchBox
                value={searchText}
                label="Search"
                placeholder="Search by User Name"
                onSubmit={handleSearch}
              />
            </div>
            <form
              className="d-flex flex-wrap mt-3"
              style={{
                marginLeft: "-16px",
              }}
              onSubmit={(e) => handleSubmit(e)}
            >
              <DateRange
                startDate={startDate}
                endDate={endDate}
                handleDateChange={handleDateChange}
              />
              <div className="px-0 align-self-end">
                <button
                  type="submit"
                  className="rf-btn rf-btn-primary rf-btn-md border-0"
                  disabled={applications.length < 1}
                >
                  Download
                </button>
              </div>
            </form>
          </div>
        )}
        <Table
          loading={isLoadingActive(loadingQueue)}
          columns={getColumns(applicationType)}
          data={getTableData(applications)}
        />
      </div>
      <Pagination
        currentPage={pageNo}
        hasNextPage={applications.length >= numberOfEntitiesPerPage}
        numberOfEntriesPerPage={numberOfEntitiesPerPage}
        onPageChange={(pageNo) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            pageNo,
          }))
        }
        onPerPageCountChange={(countPerPage) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            numberOfEntitiesPerPage: countPerPage,
            pageNo: 1,
          }))
        }
      />
    </>
  );
}
